<template>
    <Form as="" :validation-schema="schema" v-slot="{ meta, handleSubmit }">
        <bs5-base-modal :title="$t('users.assignTitle')" :visible="visible" @close="closeModal">
            <template v-slot:default>
                <form novalidate @submit.prevent="handleSubmit($event, assignDistributor)">
                    <bs5-form-field v-if="distributors?.length > 0" id="assignDistributorSelect" name="distributor"
                        :label="$t('users.distributor')" :placeholder="$t('users.chooseDistributor')"
                        :options="distributorOptions" v-model="selectedDistributorId" />
                    <div v-else class="mb-3">
                        <div class="form-label">{{ $t("users.distributor") }}</div>
                        <div class="text-secondary">
                            {{ $t("users.noDistributorAvailable") }}
                        </div>
                    </div>
                    <button type="submit" class="d-none" tabindex="-1" aria-hidden="true"></button>
                </form>
            </template>

            <template v-slot:footer>
                <bs5-cancel-button @click="closeModal" />
                <bs5-save-button :loading="saving" :disabled="!meta.valid"
                    @click="handleSubmit($event, assignDistributor)">
                    <i class="bi bi-diagram-3"></i>{{ $t("users.assign") }}
                </bs5-save-button>
            </template>
        </bs5-base-modal>
    </Form>
</template>

<script lang="ts">
import { t } from "@/helpers/i18n";
import { defineComponent, ref } from "vue";
import { Form } from "vee-validate";
import * as yup from "yup";
import userService from "@/services/user.service";
import distributorService from "@/services/distributor.service";
import { Distributor } from "@/models/distributor.model";
import { showToast } from "@/utils/toast";
import { requiredChoice } from "@/utils/validation";

export default defineComponent({
    name: "AssignDistributorModal",
    components: { Form },
    props: {
        visible: { type: Boolean, required: true },
        userId: { type: String, required: true },
    },
    emits: ["close", "distributor-assigned"],
    data() {
        return {
            distributors: ref<Distributor[]>([]),
            selectedDistributorId: ref<string>(""),
            saving: ref(false),
        };
    },
    computed: {
        schema() {
            return yup.object({
                distributor: requiredChoice(),
            });
        },
        distributorOptions(): { value: string; label: string }[] {
            return this.distributors.map(distributor => ({ value: distributor.id as string, label: distributor.name }));
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    // Wybór z poprzedniego otwarcia mógłby dotyczyć innego użytkownika
                    this.selectedDistributorId = "";
                    this.fetchDistributors();
                }
            },
        },
    },
    methods: {
        async fetchDistributors() {
            try {
                const availableDistributors = await distributorService.getDistributors();
                const assignedDistibutor = await userService.getUserDistributor(this.userId);
                this.distributors = availableDistributors.filter(item => !assignedDistibutor.some(el => el.id === item.id));

            } catch (error) {
                console.error("Error fetching distributors:", error);
            }
        },
        async assignDistributor() {
            if (!this.selectedDistributorId) {
                return;
            }

            this.saving = true;
            try {
                await userService.assignDistributor(this.userId, this.selectedDistributorId);
                this.$emit("distributor-assigned");
                this.closeModal();
                showToast(t("users.assigned"), "success");
            } catch (error) {
                showToast(t("users.assignFailed"), "error");
                console.error("Error assigning distributor:", error);
            } finally {
                this.saving = false;
            }
        },
        closeModal() {
            this.$emit("close");
        },
    },
});
</script>
